import { useCallback, useEffect, useRef, useState } from "react";
import VentasService from "../api/ventas.api";
import useCargaPeriodica from "./useCargaPeriodica";

const mismoDocumento = (a, b) =>
    a.tipo === b.tipo && a.serie === b.serie && a.pedido === b.pedido;

/**
 * Hook que carga las ventas pendientes (GET ventas/:ano/estados), las refresca en silencio
 * y cambia el estado de una fila. El backend ignora el año de la URL y usa siempre el actual.
 * Los toasts, el sonido y los textos los decide la página.
 * @param {number} ejercicio - Año del ejercicio
 * @param {{ intervaloMs?: number }} [opciones] - Refresco periódico opcional
 * @returns {{ pedidos: Array, loading: boolean, error: (string|null), cambiandoId: (string|null),
 *   cambiarEstado: Function, recargar: Function }} cambiarEstado(fila, estado) devuelve la
 *   respuesta del backend, undefined si ya hay un cambio en vuelo, y relanza el error si falla
 */
const usePedidosVenta = (ejercicio, { intervaloMs } = {}) => {
    const cargar = useCallback(() => VentasService.getEstados(ejercicio), [ejercicio]);
    const { datos, setDatos, loading, error, recargar } = useCargaPeriodica(cargar, { intervaloMs });
    const [cambiandoId, setCambiandoId] = useState(null);
    const enVueloRef = useRef(false);
    const montadoRef = useRef(true);

    useEffect(() => {
        montadoRef.current = true;
        return () => { montadoRef.current = false; };
    }, []);

    const cambiarEstado = useCallback(async (fila, estado) => {
        if (enVueloRef.current) return undefined;
        enVueloRef.current = true;
        setCambiandoId(fila.id);
        try {
            const respuesta = await VentasService.asignarEstado(fila.numero, ejercicio, fila.tipo, estado);
            if (montadoRef.current) {
                setDatos((previos) => previos.map((venta) =>
                    mismoDocumento(venta, fila) ? { ...venta, estado } : venta));
            }
            return respuesta;
        } finally {
            enVueloRef.current = false;
            if (montadoRef.current) setCambiandoId(null);
        }
    }, [ejercicio, setDatos]);

    return { pedidos: datos, loading, error, cambiandoId, cambiarEstado, recargar };
};

export default usePedidosVenta;
